/**
 * Device and Browser Information Utility
 * Detects browser, OS, and device information
 */

/**
 * Get browser name and version
 * @returns {Object} { name, version }
 */
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';

  // Detect browser
  if (userAgent.indexOf('Firefox') > -1) {
    browserName = 'Firefox';
    browserVersion = userAgent.match(/Firefox\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.indexOf('SamsungBrowser') > -1) {
    browserName = 'Samsung Browser';
    browserVersion = userAgent.match(/SamsungBrowser\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
    browserName = 'Opera';
    browserVersion = userAgent.match(/(?:Opera|OPR)\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.indexOf('Trident') > -1) {
    browserName = 'Internet Explorer';
    browserVersion = userAgent.match(/rv:(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.indexOf('Edge') > -1) {
    browserName = 'Microsoft Edge (Legacy)';
    browserVersion = userAgent.match(/Edge\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.indexOf('Edg') > -1) {
    browserName = 'Microsoft Edge';
    browserVersion = userAgent.match(/Edg\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.indexOf('Chrome') > -1) {
    browserName = 'Chrome';
    browserVersion = userAgent.match(/Chrome\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (userAgent.indexOf('Safari') > -1) {
    browserName = 'Safari';
    browserVersion = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || 'Unknown';
  }

  return { name: browserName, version: browserVersion };
};

/**
 * Get operating system name
 * @returns {string} OS name
 */
const getOS = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;

  if (userAgent.indexOf('Win') > -1) return 'Windows';
  if (userAgent.indexOf('Mac') > -1) return 'MacOS';
  if (userAgent.indexOf('Linux') > -1) return 'Linux';
  if (userAgent.indexOf('Android') > -1) return 'Android';
  if (userAgent.indexOf('like Mac') > -1) return 'iOS';
  if (platform.indexOf('iPhone') > -1) return 'iOS';
  if (platform.indexOf('iPad') > -1) return 'iOS';

  return 'Unknown';
};

/**
 * Get device type
 * @returns {string} Device type (Desktop, Mobile, Tablet)
 */
const getDeviceType = () => {
  const userAgent = navigator.userAgent;

  // Check for mobile devices
  if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    return 'Mobile';
  }

  // Check for tablets
  if (/iPad|Android/i.test(userAgent) && !/Mobile/i.test(userAgent)) {
    return 'Tablet';
  }

  // Default to desktop
  return 'Desktop';
};

/**
 * Get screen resolution
 * @returns {string} Screen resolution (e.g., "1920x1080")
 */
const getScreenResolution = () => {
  return `${window.screen.width}x${window.screen.height}`;
};

/**
 * Get device language
 * @returns {string} Language code (e.g., "en-US")
 */
const getLanguage = () => {
  return navigator.language || navigator.userLanguage || 'Unknown';
};

/**
 * Get complete device information
 * @returns {Object} Complete device info
 */
export const getDeviceInfo = () => {
  const browser = getBrowserInfo();

  return {
    browserName: browser.name,
    browserVersion: browser.version,
    os: getOS(),
    deviceType: getDeviceType(),
    screenResolution: getScreenResolution(),
    language: getLanguage(),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
  };
};

/**
 * Get device info as a JSON string for header
 * @returns {string} JSON string of device info
 */
export const getDeviceInfoHeader = () => {
  return JSON.stringify(getDeviceInfo());
};

export default {
  getDeviceInfo,
  getDeviceInfoHeader,
};
